import React, { Fragment, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowDownIcon, AlertCircleIcon, CheckCircleIcon, ZapIcon, TargetIcon, ScaleIcon, ClockIcon, ListIcon, BoxIcon } from 'lucide-react';
/**
 * TextStructureSchema — V4 (TAA audit §6 / §11.8 / S4)
 *
 * Visualizes the 4 most common Dutch text structures as a compact, labeled
 * diagram. Used as a `stimulusAid` (when the schema IS the question stimulus)
 * or as a `supportAid` (when shown after a wrong answer).
 *
 * Templates:
 *   - "probleem-oplossing":  [Probleem] → [Oplossing]
 *   - "oorzaak-gevolg":      [Oorzaak]  → [Gevolg]
 *   - "vergelijking":        [Ding A]  ⇄ [Ding B]   (2 boxes, double arrow)
 *   - "chronologisch":       [Stap 1] → [Stap 2] → [Stap 3]   (timeline)
 *   - "opsomming":           [Punt 1] → [Punt 2] → [Punt 3]   (list)
 *
 * Optional `labels` overrides the default Dutch box labels.
 * Optional `highlightIndex` highlights one specific box.
 */
type StructureKind = 'probleem-oplossing' | 'oorzaak-gevolg' | 'vergelijking' | 'chronologisch' | 'opsomming';
interface TextStructureSchemaProps {
  structure: StructureKind;
  labels?: string[];
  highlightIndex?: number;
  label?: string;
  mode?: string;
}
interface Template {
  defaultLabels: string[];
  icons: BoxIcon[];
  colors: string[];
  // 'flow' = single arrows, 'compare' = double arrow, 'timeline' = numbered chain, 'list' = vertical/horizontal list
  variant: 'flow' | 'compare' | 'timeline' | 'list';
  title: string;
}
const TEMPLATES: Record<StructureKind, Template> = {
  'probleem-oplossing': {
    title: 'Probleem-oplossing',
    defaultLabels: ['Probleem', 'Oplossing'],
    icons: [AlertCircleIcon, CheckCircleIcon],
    colors: ['bg-red-50 border-red-300 text-red-800', 'bg-green-50 border-green-300 text-green-800'],
    variant: 'flow'
  },
  'oorzaak-gevolg': {
    title: 'Oorzaak-gevolg',
    defaultLabels: ['Oorzaak', 'Gevolg'],
    icons: [ZapIcon, TargetIcon],
    colors: ['bg-amber-50 border-amber-300 text-amber-800', 'bg-blue-50 border-blue-300 text-blue-800'],
    variant: 'flow'
  },
  vergelijking: {
    title: 'Vergelijking',
    defaultLabels: ['Ding A', 'Ding B'],
    icons: [ScaleIcon, ScaleIcon],
    colors: ['bg-indigo-50 border-indigo-300 text-indigo-800', 'bg-purple-50 border-purple-300 text-purple-800'],
    variant: 'compare'
  },
  chronologisch: {
    title: 'Chronologisch',
    defaultLabels: ['Eerst', 'Daarna', 'Ten slotte'],
    icons: [ClockIcon, ClockIcon, ClockIcon],
    colors: ['bg-sky-50 border-sky-300 text-sky-800', 'bg-sky-100 border-sky-400 text-sky-900', 'bg-sky-200 border-sky-500 text-sky-900'],
    variant: 'timeline'
  },
  opsomming: {
    title: 'Opsomming',
    defaultLabels: ['Punt 1', 'Punt 2', 'Punt 3'],
    icons: [ListIcon, ListIcon, ListIcon],
    colors: ['bg-teal-50 border-teal-300 text-teal-800', 'bg-teal-50 border-teal-300 text-teal-800', 'bg-teal-50 border-teal-300 text-teal-800'],
    variant: 'list'
  }
};
const HIGHLIGHT_RING = 'ring-2 ring-offset-2 ring-amber-400 shadow-md';
function Box({
  text,
  Icon,
  colorClass,
  highlighted,
  index
}: {
  text: string;
  Icon: BoxIcon;
  colorClass: string;
  highlighted: boolean;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.06,
    duration: 0.25
  }} className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 px-3 py-2 min-w-[88px] text-center ${colorClass} ${highlighted ? HIGHLIGHT_RING : ''}`} data-id="element-12856">
      <Icon className="h-4 w-4" aria-hidden="true" data-id="element-12857" />
      <span className="text-xs font-semibold leading-tight" data-id="element-12858">{text}</span>
    </motion.div>;
}
export function TextStructureSchema({
  structure,
  labels,
  highlightIndex,
  label
}: TextStructureSchemaProps) {
  const template = TEMPLATES[structure];
  if (!template) return null;
  const finalLabels = labels && labels.length === template.defaultLabels.length ? labels : template.defaultLabels;
  const isHighlighted = (i: number) => highlightIndex === i;
  return <figure className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white p-4" aria-label={`Tekststructuur: ${template.title}`} data-id="element-12859">
      <figcaption className="text-xs font-medium uppercase tracking-wide text-slate-500" data-id="element-12860">
        {label ?? template.title}
      </figcaption>

      {template.variant === 'flow' && <div className="flex items-center gap-2" data-id="element-12861">
          <Box text={finalLabels[0]} Icon={template.icons[0]} colorClass={template.colors[0]} highlighted={isHighlighted(0)} index={0} data-id="element-12862" />
          <ArrowRightIcon className="h-5 w-5 text-slate-400" aria-hidden="true" data-id="element-12863" />
          <Box text={finalLabels[1]} Icon={template.icons[1]} colorClass={template.colors[1]} highlighted={isHighlighted(1)} index={1} data-id="element-12864" />
        </div>}

      {template.variant === 'compare' && <div className="flex items-center gap-3" data-id="element-12865">
          <Box text={finalLabels[0]} Icon={template.icons[0]} colorClass={template.colors[0]} highlighted={isHighlighted(0)} index={0} data-id="element-12866" />
          <div className="flex flex-col items-center text-slate-400" aria-hidden="true" data-id="element-12867">
            <ArrowRightIcon className="h-4 w-4" data-id="element-12868" />
            <ArrowRightIcon className="h-4 w-4 -mt-1 rotate-180" data-id="element-12869" />
          </div>
          <Box text={finalLabels[1]} Icon={template.icons[1]} colorClass={template.colors[1]} highlighted={isHighlighted(1)} index={1} data-id="element-12870" />
        </div>}

      {template.variant === 'timeline' && <div className="flex flex-wrap items-center justify-center gap-2" data-id="element-12871">
          {finalLabels.map((labelText, i) => {
        const Icon = template.icons[i] ?? template.icons[template.icons.length - 1];
        const colorClass = template.colors[i] ?? template.colors[template.colors.length - 1];
        return <Fragment key={i} data-id="element-12872">
                <div className="flex flex-col items-center gap-1" data-id="element-12873">
                  <span className="text-[10px] font-mono font-bold text-slate-500" data-id="element-12874">
                    {i + 1}
                  </span>
                  <Box text={labelText} Icon={Icon} colorClass={colorClass} highlighted={isHighlighted(i)} index={i} data-id="element-12875" />
                </div>
                {i < finalLabels.length - 1 && <ArrowRightIcon className="h-5 w-5 text-slate-400 sm:block" aria-hidden="true" data-id="element-12876" />}
              </Fragment>;
      })}
        </div>}

      {template.variant === 'list' && <div className="flex flex-col gap-2 w-full max-w-[200px]" data-id="element-12877">
          {finalLabels.map((labelText, i) => {
        const Icon = template.icons[i] ?? template.icons[template.icons.length - 1];
        const colorClass = template.colors[i] ?? template.colors[template.colors.length - 1];
        return <Box key={i} text={labelText} Icon={Icon} colorClass={colorClass} highlighted={isHighlighted(i)} index={i} data-id="element-12878" />;
      })}
        </div>}
    </figure>;
}
// Default export for lazy-import compatibility (none required by the registry,
// but keeps options open).
export default TextStructureSchema;